import { FC, useState } from 'react';
import { Field, FieldProps, Formik } from 'formik';
// @ts-ignore
import { RadioGroup } from 'react-radio-buttons';
import {
  AlertComponent,
  Box,
  RadioButton,
  FieldRow,
  Flatfile,
  Flex,
  Modal,
  StyledFormGroup,
  StyledFormSelectFormik,
  WrappedQuery,
} from '.';
import { client } from '../../apollo';
import { ALL_INSTITUTION_TYPES, FILTERED_INSTITUTIONS, GET_MINISTRIES, UPSERT_INSTITUTIONS } from '../../graphql';
import {
  flatfileInstitutionValidator,
  formatFormInstitutionToDBInstitutionForBulkUpload,
  formatSelectValue,
  parseErrorMessage,
  stripSelectValues,
} from '../../utils';
import { MULTIPLE_INSTITUTIONS } from '../../consts';
import { commonInstitutionFields } from '../../consts/flatfileFields';
import { Label } from '../../typography';
import { OperationVariables, QueryResult } from '@apollo/client';

interface Props {
  setShowUploadModal: (value: boolean) => void;
  showUploadModal: boolean;
}

interface UploadFormValues {
  ministryId?: {
    label: string;
    value: string | number;
  };
  typeId?: {
    label: string;
    value: string | number;
  };
  uploadData?: any;
  year?: string;
  tenDayView?: boolean;
  updateOrNew?: string;
}

const INITIAL_VALUES = {
  ministryId: undefined,
  typeId: undefined,
  uploadData: undefined,
  year: undefined,
  tenDayView: false,
  updateOrNew: 'update',
};

const UploadInstitutionsModal: FC<Props> = ({ showUploadModal, setShowUploadModal }) => {
  const [selectedMinistry, setSelectedMinistry] = useState<any>(undefined);
  const [selectedType, setSelectedType] = useState<any>(undefined);

  return (
    <Modal open={showUploadModal} onClose={() => setShowUploadModal(!showUploadModal)} heading="Upload Data">
      <Formik
        initialValues={INITIAL_VALUES}
        onSubmit={async values => {
          const copyOfValues = Object.assign({}, values);
          const formattedValues: any = stripSelectValues(copyOfValues);
          const institutions = formattedValues.uploadData
            ? formattedValues.uploadData.map((a: any) => {
                const institution = formatFormInstitutionToDBInstitutionForBulkUpload(a, formattedValues.tenDayView);
                return institution;
              })
            : [];

          return await client
            .mutate({
              mutation: UPSERT_INSTITUTIONS,
              variables: {
                inputs: {
                  ministryId: formattedValues.ministryId,
                  typeId: formattedValues.typeId,
                  year: formattedValues.year,
                  tenDayView: formattedValues.tenDayView,
                  updateOrNew: formattedValues.updateOrNew,
                  values: institutions,
                },
              },
              refetchQueries: [
                {
                  query: FILTERED_INSTITUTIONS,
                  variables: { input: { first: 10, status: 'ACTIVE' } },
                },
              ],
            })
            .then(() => 'Success!')
            .catch(e => parseErrorMessage((e && e.message) || e));
        }}
        render={formikProps => {
          const { values } = formikProps;

          return (
            <Flex direction="column" size="1" px={3}>
              <Flex direction="column" size="1" flexWrap="nowrap" justify="space-around" minHeight={170}>
                <WrappedQuery query={GET_MINISTRIES} overrideStates={true}>
                  {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                    if (data && data.ministries && !selectedMinistry) {
                      const first = data.ministries[0];

                      first && setSelectedMinistry({ label: first.name, value: first.id });
                      first && formikProps.setFieldValue('ministryId', { label: first.name, value: first.id });
                    }

                    return (
                      <Field
                        name="ministryId"
                        render={({ field }: FieldProps<UploadFormValues>) => (
                          <StyledFormSelectFormik
                            {...field}
                            field={field}
                            label="Ministry"
                            form={formikProps}
                            placeholder={loading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
                            isDisabled={loading}
                            isClearable={true}
                            options={
                              loading || error
                                ? []
                                : data.ministries.map((item: any) =>
                                    formatSelectValue({
                                      label: item.name,
                                      value: item.id,
                                    })
                                  )
                            }
                            mr={4}
                          />
                        )}
                      />
                    );
                  }}
                </WrappedQuery>
                <br />
                <WrappedQuery
                  query={ALL_INSTITUTION_TYPES}
                  variables={{
                    input: {
                      ministryId: selectedMinistry && selectedMinistry.value,
                    },
                  }}
                  overrideStates={true}
                >
                  {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                    if (data && data.institutionTypes && !selectedType) {
                      const first = data.institutionTypes[3];

                      first && setSelectedType({ label: first.name, value: first.id });
                      first && formikProps.setFieldValue('typeId', { label: first.name, value: first.id });
                    }

                    return (
                      <Field
                        name="typeId"
                        render={({ field }: FieldProps<UploadFormValues>) => (
                          <StyledFormSelectFormik
                            {...field}
                            field={field}
                            label="Institution Type"
                            form={formikProps}
                            placeholder={loading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
                            isDisabled={loading}
                            isClearable={true}
                            options={
                              loading || error
                                ? []
                                : data &&
                                  data.institutionTypes.map((item: any) =>
                                    formatSelectValue({
                                      label: item.name,
                                      value: item.id,
                                    })
                                  )
                            }
                            mr={4}
                          />
                        )}
                      />
                    );
                  }}
                </WrappedQuery>
                <FieldRow>
                  {values && values.typeId && (values.typeId as any).label === 'ECD Service' && (
                    <>
                      <FieldRow flex={0.25}>
                        <Field
                          name="year"
                          render={({ field }: FieldProps<UploadFormValues>) => (
                            <StyledFormGroup {...field} field={field} form={formikProps} type="number" label="Year" />
                          )}
                        />
                      </FieldRow>
                      <FieldRow flex={0.25}>
                        <Field
                          name={`tenDayView`}
                          render={({ field }: FieldProps<UploadFormValues>) => (
                            <StyledFormGroup
                              {...field}
                              field={field}
                              form={formikProps}
                              label="10 Day View"
                              type="checkbox"
                              defaultChecked={formikProps.values.tenDayView}
                              mr={2}
                              ml={5}
                              wrapperWidth="40px"
                              width="40px"
                            />
                          )}
                        />
                      </FieldRow>
                    </>
                  )}
                  <FieldRow>
                    <FieldRow flex={0.5}>
                      <Field
                        name="updateOrNew"
                        render={({ field }: FieldProps<UploadFormValues>) => (
                          <Box {...formikProps} flexDirection={'column'}>
                            <Label htmlFor="updateOrNew">Select option:</Label>
                            <RadioGroup
                              value={formikProps.values.updateOrNew}
                              onChange={(value: string) => {
                                formikProps.setFieldValue('updateOrNew', value);
                              }}
                              horizontal={true}
                            >
                              <RadioButton value="update">Update</RadioButton>
                              <RadioButton value="new">New</RadioButton>
                            </RadioGroup>
                          </Box>
                        )}
                      />
                    </FieldRow>
                  </FieldRow>
                </FieldRow>
                <br />
                <AlertComponent
                  title={`Instructions`}
                  message={`
                  Please upload a maximum of 1500 records at a time

                  Note that CSV values are sensitive to:
                    - Typographical errors
                    - Text Case
                    - Naming conventions
                `}
                />
              </Flex>

              {/* flatfile buttons */}
              <Field
                name="uploadData"
                render={({ field }: FieldProps<UploadFormValues>) => (
                  <Flatfile
                    csvFileData={MULTIPLE_INSTITUTIONS}
                    csvFileName={`InstitutionsTemplate.csv`}
                    flatFileDataType="Institutions"
                    flatFileFields={commonInstitutionFields}
                    flatFileSubmitHandler={async (results: any) => {
                      // check total number of entries
                      if (results.length > 1500) {
                        throw new Error('Client error - Please only upload a maximum of 1500 stakeholders at a time');
                      }

                      formikProps.values.uploadData = results;

                      return await formikProps.handleSubmit();
                    }}
                    flatFileValidators={flatfileInstitutionValidator}
                    close={() => setShowUploadModal(!showUploadModal)}
                  />
                )}
              />
            </Flex>
          );
        }}
      />
    </Modal>
  );
};

export default UploadInstitutionsModal;
